// What a picture or a video costs in gems, as the cost bars show it. The backend charges; these
// figures only mirror it, and where they disagree the backend wins. Kept apart from
// helpers/helpers.jsx (which re-exports them) so the unit tests can import them.
//
// Video prices mirror v2's db/migrations/5227_video_engines.sql (core.video_options) and v1's
// myapp/video3_service.py, which agree:
//   - v4 (video4): 15 from a picture, 13 from a prompt; High adds nothing when a video is made
//     (product decision 3.1: charge 15, show 15). Extending a High video costs 10 more, which is
//     ExtendModal's figure, not this one. Sound +3.
//   - v4-voice (video5): by length, 48 fps and resolution (ltxGemsFor), sound included.
//   - From a prompt, every version also pays for its first picture at that style's own price
//     (v2 orders/clip.py base_priced, v1 video3.generate_video_image): 1 on the older styles, 2 on
//     an XL one, a Flame model's own price on Flame.

import { isLtx, ltxGemsFor } from '../helpers/videoVersions.js'

// Flame image price per model — mirrors `gem_cost` in the backend's var/models_data.py (4
// unless a model sets its own; none does since Hardcore Flame dropped to 4 on 2026-09-24).
const FLAME_IMAGE_GEMS = {}
export const flameImageGems = (model) => FLAME_IMAGE_GEMS[model] ?? 4

const isFlame = (model) => !!model?.includes('Flame')

// One picture at standard quality, no extras: a Flame model's price, 2 on an XL style, 1 on the
// older ones (Inputs.jsx's cost bar, for one image). An unknown model counts as XL, the price the
// video panel assumed before it knew the style.
export function oneImageGems(model) {
  if (isFlame(model)) return flameImageGems(model)
  if (!model) return 2
  return model.includes('XL') ? 2 : 1
}

const V4_FROM_PICTURE = 15
const V4_FROM_PROMPT = 13
const OLDER_FROM_PICTURE = 20
const OLDER_FROM_PROMPT = 18
const SOUND = 3

export function videoGemCost({
  videoVersion,
  audioEnabled,
  charImageVideo,
  model,
  hasReferenceImage,
  videoLength,
  videoFps,
  videoResolution,
} = {}) {
  const fromPrompt = !hasReferenceImage
  // v4-voice renders sound in the same job and has no quality tier: the backend ignores those
  // fields, so their surcharges are left out here too.
  const clip = isLtx(videoVersion)
    ? ltxGemsFor(videoLength, videoFps, videoResolution)
    : videoVersion === 'video4'
      ? fromPrompt
        ? V4_FROM_PROMPT
        : V4_FROM_PICTURE
      : fromPrompt
        ? OLDER_FROM_PROMPT
        : OLDER_FROM_PICTURE
  const firstPicture = fromPrompt ? oneImageGems(model) : 0
  const sound = !isLtx(videoVersion) && audioEnabled ? SOUND : 0
  return clip + firstPicture + sound + (charImageVideo ? 1 : 0)
}
